import {
  ContextDashboardIsUsedIn,
  DashboardApiError,
  DashboardColor,
  DashboardFiltersLinkCountDict,
  DashboardModes,
  EditModeDashboardDataProps,
  FullscreenWidgetData,
  LoadedDashboard,
  MinimalFieldRepresentation,
  OnWidgetDrilldown,
  OpenFullscreenWidget,
  RememberedColorsByDashboardId,
  ReportFromSelectedWidgetId,
} from './types';
import {
  AnyWidgetAttributes,
  APIBarChartAttributes,
  APIDashboardAttributes,
  APIDashboardFilterParameter,
  APIFieldType,
  APINumberWidgetAttributes,
  APITableWidgetAttributes,
  ConditionalFormattingLimit,
  DashboardFilterValues,
  DashboardSettings,
  DataSourceType,
  FilterSuggestionSecondaryInformation,
  HexColor,
  isGremlinSearch,
  ReportColumnType,
  SupportedDashboardFilterReferenceType,
  SupportedDashboardFilterType,
} from '@ardoq/api-types';
import { getPieChartDataSegments } from './widgets/categoricalCharts/PieChartWidget';
import { getStackedBarChartData } from './widgets/timelineWidgets/getStackedBarChartOptions';
import { qualitativeTheme } from '@ardoq/color-picker';
import { v4 as uuidv4 } from 'uuid';
import { widgetOperations } from './widgets/operations';
import { dateRangeOperations } from '@ardoq/date-range';
import { isEmpty, isObjectLike } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { currentDate } from '@ardoq/date-time';
import { DashboardBuilderWidget } from './widgets/types';
import { getBarChartSegments } from './widgets/categoricalCharts/BarChartWidget';
import { DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS } from './consts';
import { ARDOQ_DEFAULT_LOCALE, Locale } from '@ardoq/locale';
import { IconName } from '@ardoq/icons';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { EnhancedScopeData } from '@ardoq/data-model';
import { ViewModeWidgetPropsArrayWithType } from './widgets/widgetPropTypes';
import { getViewModeNoteWidgetProps } from './widgets/NoteWidget/getNoteWidgetProps';
import { getViewModePowerBiIframeWidgetProps } from './widgets/PowerBiIframeWidget/getPowerBiIframeWidgetProps';
import type { I18nLocale } from '@ardoq/i18n';
import {
  getUsageMetricsModeWidgetProps,
  getViewModeChartWidgetProps,
} from './widgets/getViewModeWidgetProps';
import { Layout } from 'react-grid-layout';

export const isDashboardApiError = (
  apiError: any
): apiError is DashboardApiError =>
  isObjectLike(apiError) && 'message' in apiError && 'traceId' in apiError;

type TableWidgetWithEditPermissions = APITableWidgetAttributes & {
  permissions?: {
    canExploreDatasource: boolean;
    canEditWidget: boolean;
  };
};

const mergeDateRangeFieldsInTableWidget = (
  tableWidget: TableWidgetWithEditPermissions
) => {
  const scopeDataWithDateRanges = tableWidget.scopeData
    ? dateRangeOperations.getMergedDateRangeFieldForScopeData(
        tableWidget.scopeData
      )
    : null;
  const dateRangeFieldNames = scopeDataWithDateRanges
    ? new Set(
        Array.from(scopeDataWithDateRanges.dateRangeFieldMap.values(), val =>
          Object.values(val).flatMap(dateRangeField => [
            dateRangeField.start.name,
            dateRangeField.end.name,
          ])
        ).flat()
      )
    : new Set();

  const tableFieldsWithDateRanges = tableWidget.tableFields.reduce<
    APITableWidgetAttributes['tableFields']
  >((mergedFields, tableField) => {
    const isDateRangeField = dateRangeFieldNames.has(tableField.name);

    if (isDateRangeField) {
      if (dateRangeOperations.fieldNameIsDateRangeStartField(tableField.name)) {
        mergedFields.push({
          ...tableField,
          label: dateRangeOperations.extractDateRangeFieldLabel(
            tableField.label
          ),
          name: dateRangeOperations.extractDateRangeFieldName(tableField.name)!,
        });
      }
    } else {
      mergedFields.push(tableField);
    }
    return mergedFields;
  }, []);

  const mergedData =
    (scopeDataWithDateRanges &&
      tableWidget.data?.map(entity =>
        dateRangeOperations.mergeDateRangeFieldsForEntity(
          entity,
          scopeDataWithDateRanges.dateRangeFieldMap
        )
      )) ??
    null;
  const fieldsWithDateRanges = tableWidget.fields?.reduce<string[]>(
    (dateRangeFields, field) => {
      const isDateRangeField = dateRangeFieldNames.has(field);

      if (isDateRangeField) {
        if (dateRangeOperations.fieldNameIsDateRangeStartField(field)) {
          dateRangeFields.push(
            dateRangeOperations.extractDateRangeFieldName(field)
          );
        }
      } else {
        dateRangeFields.push(field);
      }

      return dateRangeFields;
    },
    []
  );
  return {
    ...tableWidget,
    fields: fieldsWithDateRanges,
    data: mergedData,
    scopeData: scopeDataWithDateRanges,
    tableFields: tableFieldsWithDateRanges,
  };
};

type WidgetDataPoints = {
  widgetId: string;
  dataPoints: string[];
};

type AcceptNonClashingColorsInWidgetParams = {
  acceptedColors: Record<string, HexColor>;
  dataPoints: string[];
  savedColors: Record<string, HexColor>;
  rememberedColors: Record<string, HexColor>;
};

const acceptNonClashingColorsInWidget = ({
  acceptedColors,
  dataPoints,
  savedColors,
  rememberedColors,
}: AcceptNonClashingColorsInWidgetParams): Record<string, HexColor> =>
  dataPoints.reduce(
    (state, dataPoint) => {
      const color = rememberedColors[dataPoint];
      return color && !state.placedColors.includes(color)
        ? {
            acceptedColors: { ...state.acceptedColors, [dataPoint]: color },
            placedColors: [...state.placedColors, color],
          }
        : state;
    },
    {
      acceptedColors,
      placedColors: dataPoints
        .map(dataPoint => savedColors[dataPoint] ?? acceptedColors[dataPoint])
        .filter(isTruthy),
    }
  ).acceptedColors;

type ToNonClashingRememberedColorsParams = {
  widgets: WidgetDataPoints[];
  savedColors: Record<string, HexColor>;
  rememberedColors: Record<string, HexColor>;
};

const toNonClashingRememberedColors = ({
  widgets,
  savedColors,
  rememberedColors,
}: ToNonClashingRememberedColorsParams): Record<string, HexColor> =>
  widgets.reduce<Record<string, HexColor>>(
    (acceptedColors, { dataPoints }) =>
      acceptNonClashingColorsInWidget({
        acceptedColors,
        dataPoints,
        savedColors,
        rememberedColors,
      }),
    {}
  );

const toAutoAssignedColor = ([dataPoint, color]: [
  string,
  HexColor,
]): DashboardColor => ({
  dataPoint,
  color,
  isManuallyAssigned: false,
  widgetsUsingThis: [],
});

type ApplyColorsToFieldsParams = {
  widgets: DashboardBuilderWidget[];
  colors: EditModeDashboardDataProps['settings']['colors'] | undefined;
  paletteColors: HexColor[] | null;
  rememberedColors: Record<string, HexColor>;
};

export const applyColorsToFields = ({
  widgets,
  colors,
  paletteColors,
  rememberedColors,
}: ApplyColorsToFieldsParams): Array<DashboardColor> => {
  // These are the data points that we need to color
  const relevantDataPointsOnwWidgets = widgets
    .filter(widgetOperations.isColorableWidget)
    .map(widget => {
      if (widgetOperations.isReadPieChartData(widget)) {
        const { chartData } = getPieChartDataSegments(
          widget.data,
          widget.numberSlices
        );
        return {
          widgetId: widget._id,
          dataPoints: chartData.map(({ name }) => name),
        };
      } else if (widgetOperations.isReadStackedBarChartData(widget)) {
        if (!widget.data) {
          return {
            widgetId: widget._id,
            dataPoints: [],
          };
        }
        const { combinedSeries } = getStackedBarChartData(
          widget.data,
          widget.numberSlices ??
            DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
          ARDOQ_DEFAULT_LOCALE // Locale is not relevant for color assignment, so we can just pass the default here
        );
        return {
          widgetId: widget._id,
          dataPoints: combinedSeries.map(({ name }) => name),
        };
      } else if (widgetOperations.isReadLineChartData(widget)) {
        return {
          widgetId: widget._id,
          dataPoints: widget.fields.map(fieldName => fieldName),
        };
      }
      // Bar chart
      const barchartSeries = (widget as APIBarChartAttributes).data
        ? getBarChartSegments(
            widget.data!,
            (widget as APIBarChartAttributes).numberSlices
          )
        : { segments: [] };
      return {
        widgetId: widget._id,
        dataPoints: barchartSeries.segments.map(({ name }) => name),
      };
    })
    .sort((a, b) => (a.widgetId < b.widgetId ? 1 : -1)); // sorting by widgetId so that colors doesn't change when a widget is moved

  const dataPointSet = new Set(
    relevantDataPointsOnwWidgets.flatMap(({ dataPoints }) => dataPoints)
  );

  // use the colors that have been saved to dashboard already
  const savedColorsForDataPoint =
    colors
      ?.filter(({ dataPoint }) => dataPointSet.has(dataPoint))
      .map(dataColor => ({
        ...dataColor,
        widgetsUsingThis: [],
      })) ?? [];
  const dataPointsWithSavedColor = new Set(
    savedColorsForDataPoint.map(({ dataPoint }) => dataPoint)
  );

  const colorPalette = paletteColors ?? qualitativeTheme.paletteColors;

  const applicableRememberedColors = Object.entries(rememberedColors).filter(
    ([dataPoint, color]) =>
      !dataPointsWithSavedColor.has(dataPoint) && colorPalette.includes(color)
  );

  const shownRememberedColors = Object.entries(
    toNonClashingRememberedColors({
      widgets: relevantDataPointsOnwWidgets,
      savedColors: Object.fromEntries(
        savedColorsForDataPoint.map(({ dataPoint, color }) => [
          dataPoint,
          color,
        ])
      ),
      rememberedColors: Object.fromEntries(applicableRememberedColors),
    })
  ).map(toAutoAssignedColor);

  const hiddenRememberedColors = applicableRememberedColors
    .filter(([dataPoint]) => !dataPointSet.has(dataPoint))
    .map(toAutoAssignedColor);

  const existingColorsForDataPoint = [
    ...savedColorsForDataPoint,
    ...shownRememberedColors,
  ];

  // count the number of times a color has been used in the dashboard,
  // including remembered colors of data points that are not currently shown
  const usedColorCount = [
    ...existingColorsForDataPoint,
    ...hiddenRememberedColors,
  ].reduce<Record<string, number>>((colorsWithCount, { color }) => {
    colorsWithCount[color] = (colorsWithCount[color] || 0) + 1;
    return colorsWithCount;
  }, {});

  // set up a queue of colors to pick the least used colors
  let colorQueue = [
    ...colorPalette
      .filter(color => !(color in usedColorCount))
      .map(color => ({ color, count: 0 })),
    ...Object.entries(usedColorCount)
      .filter(([color]) => colorPalette.includes(color))
      .map(([color, count]) => ({
        color,
        count,
      })),
  ].sort((a, b) => a.count - b.count);

  return relevantDataPointsOnwWidgets.reduce<
    EditModeDashboardDataProps['settings']['colors']
  >((newColorMap, { dataPoints, widgetId }) => {
    // Make a set of colors that has been set already by data points in other widgets that's also in this widget
    const colorsUsedInWidget = new Set(
      dataPoints.map(
        dataPointInWidget =>
          newColorMap.find(({ dataPoint }) => dataPointInWidget === dataPoint)
            ?.color
      )
    );
    return dataPoints.reduce<EditModeDashboardDataProps['settings']['colors']>(
      (innerMap, dataPoint) => {
        if (
          innerMap.find(
            ({ dataPoint: innerMapDataPoint }) =>
              innerMapDataPoint === dataPoint
          )
        ) {
          const existingEntry = innerMap.find(
            ({ dataPoint: innerMapDataPoint }) =>
              innerMapDataPoint === dataPoint
          )!;
          return [
            ...innerMap.filter(
              ({ dataPoint: innerMapDataPoint }) =>
                innerMapDataPoint !== dataPoint
            ),
            {
              ...existingEntry,
              widgetsUsingThis: [...existingEntry.widgetsUsingThis, widgetId],
            },
          ];
        }

        const { color, count } =
          colorQueue.find(
            ({ color }) => !colorsUsedInWidget.has(color) // find the least used color that is not used for this widget
          ) ?? colorQueue[0]; // fallback to the least used color

        colorQueue = [
          ...colorQueue.filter(item => item.color !== color),
          { color, count: count + 1 }, // increase count and put the color at the back of the queue
        ];

        return [
          ...innerMap,
          {
            dataPoint,
            color,
            widgetsUsingThis: [widgetId],
            isManuallyAssigned: false,
          },
        ];
      },
      newColorMap
    );
  }, existingColorsForDataPoint);
};

const mapDashboardAPIColors = (
  colors: DashboardSettings['colors'] | undefined
): Array<DashboardColor> =>
  colors && Array.isArray(colors)
    ? colors.map(({ dataPoint, color }) => ({
        dataPoint,
        color,
        isManuallyAssigned: true,
        widgetsUsingThis: [],
      }))
    : [];

const mapNumberWidgetLimits = (
  limits: APINumberWidgetAttributes['limits']
): ConditionalFormattingLimit[] =>
  limits?.map(limit => {
    const above = limit.above;
    return {
      ...limit,
      above: typeof above === 'string' ? parseInt(above, 10) : above,
      id: uuidv4(),
    };
  }) ?? [];

const mergeWidgetFieldsIfDateRange = (
  fields: string[],
  widget: AnyWidgetAttributes
): string[] => {
  const tableFields = 'tableFields' in widget ? widget.tableFields : undefined;
  if (!tableFields?.length) {
    return dateRangeOperations.mergeDateRangeFieldNames(fields);
  }
  const labelByName = new Map(
    tableFields.map(field => [field.name, field.label])
  );
  return dateRangeOperations.mergeDateRangeFieldNamesWithLabelValidation(
    fields,
    labelByName
  );
};

export const transformDashboardAPIData = (
  dashboard: APIDashboardAttributes
): LoadedDashboard => {
  // Any transformation that needs to be done on the dashboard data before it's rendered in either ardoq-core or Discover should be done here

  const dashboardTransformedWidgets = {
    ...dashboard,
    widgets: dashboard.widgets.map(widget => {
      if (widgetOperations.isStaticContentWidgetData(widget)) return widget;
      const isTableWidgetWithScopeData =
        widgetOperations.isReadTableWidgetData(widget) &&
        Boolean(widget.scopeData);
      const widgetWithMergedFields =
        'fields' in widget && !isTableWidgetWithScopeData
          ? {
              ...widget,
              fields: mergeWidgetFieldsIfDateRange(widget.fields, widget),
            }
          : widget;
      if (
        widgetOperations.isReadTableWidgetData(widgetWithMergedFields) &&
        widgetWithMergedFields.scopeData
      ) {
        return mergeDateRangeFieldsInTableWidget(widgetWithMergedFields);
      } else if (
        widgetOperations.isReadNumberWidgetData(widgetWithMergedFields)
      ) {
        return {
          ...widgetWithMergedFields,
          limits: mapNumberWidgetLimits(widgetWithMergedFields.limits),
          isAbbreviated: widgetWithMergedFields.isAbbreviated ?? false,
        };
      }
      return widgetWithMergedFields;
    }),
  };

  return {
    ...dashboardTransformedWidgets,
    settings: {
      ...dashboardTransformedWidgets.settings,
      colors: applyColorsToFields({
        widgets: dashboardTransformedWidgets.widgets,
        colors: mapDashboardAPIColors(
          dashboardTransformedWidgets.settings?.colors
        ),
        paletteColors: dashboardTransformedWidgets.paletteColors ?? null,
        rememberedColors: {},
      }),
    },
    _time: currentDate(),
  };
};

const isManuallyAssignedColor = ({ isManuallyAssigned }: DashboardColor) =>
  isManuallyAssigned;

/**
 * Reassigns the colors of a loaded dashboard, reusing the remembered colors
 * (data point -> color) of earlier loads where possible.
 */
const applyRememberedColors = (
  dashboard: LoadedDashboard,
  rememberedColors: Record<string, HexColor>
): LoadedDashboard => ({
  ...dashboard,
  settings: {
    ...dashboard.settings,
    colors: applyColorsToFields({
      widgets: dashboard.widgets,
      colors: dashboard.settings.colors.filter(isManuallyAssignedColor),
      paletteColors: dashboard.paletteColors ?? null,
      rememberedColors,
    }),
  },
});

const toColorEntry = ({ dataPoint, color }: DashboardColor) => [
  dataPoint,
  color,
];

type DashboardWithRememberedColors = {
  dashboard: LoadedDashboard;
  rememberedColorsByDashboardId: RememberedColorsByDashboardId;
};

/**
 * Recolors a loaded dashboard with the remembered colors of earlier loads of
 * the same dashboard, and remembers the automatically assigned colors of this
 * load.
 */
export const rememberDashboardColors = (
  dashboard: LoadedDashboard,
  rememberedColorsByDashboardId: RememberedColorsByDashboardId
): DashboardWithRememberedColors => {
  const rememberedColors = rememberedColorsByDashboardId[dashboard._id] ?? {};
  const recoloredDashboard = applyRememberedColors(dashboard, rememberedColors);
  return {
    dashboard: recoloredDashboard,
    rememberedColorsByDashboardId: {
      ...rememberedColorsByDashboardId,
      [dashboard._id]: {
        ...rememberedColors,
        ...Object.fromEntries(
          recoloredDashboard.settings.colors
            .filter(color => !isManuallyAssignedColor(color))
            .map(toColorEntry)
        ),
      },
    },
  };
};

export const getDashboardFiltersLinkCountDict = (
  globalFilterParameters: APIDashboardFilterParameter[],
  widgets: AnyWidgetAttributes[] | DashboardBuilderWidget[]
) => {
  const connectCount: DashboardFiltersLinkCountDict = {};
  const fieldApiNameToParameterIdMap = new Map<string, string>();

  for (const { id, fieldApiName } of globalFilterParameters) {
    connectCount[id] = { total: 0, warnings: 0, widgetIds: [] };
    if (fieldApiName) {
      fieldApiNameToParameterIdMap.set(fieldApiName, id);
    }
  }

  for (const widget of widgets) {
    // skip irrelevant widgets early.
    if (
      !widgetOperations.isChartTypeSupportDashboardFilters(widget) ||
      isEmpty(widget.globalFilterBindings)
    ) {
      continue;
    }

    for (const [fieldApiName, linkedParameterId] of Object.entries(
      widget.globalFilterBindings
    )) {
      const supposeLinkedParameterId =
        fieldApiNameToParameterIdMap.get(fieldApiName);

      if (linkedParameterId === supposeLinkedParameterId) {
        connectCount[linkedParameterId].total++;
        connectCount[linkedParameterId].widgetIds.push(widget._id);
      }

      // connectCount is based on the current globalFilterParameters
      // so it won't include a deleted dashboard filter (which may still
      // be referenced by a warning)
      if (
        linkedParameterId in connectCount &&
        widget.globalFilterWarnings?.[linkedParameterId]
      ) {
        connectCount[linkedParameterId].warnings++;
      }
    }
  }

  return connectCount;
};

export const getDashboardColors = (
  dashboardData: (APIDashboardAttributes & { _time: Date }) | null
): Record<string, HexColor> => {
  if (!dashboardData) return {};
  return (
    dashboardData.settings?.colors.reduce<Record<string, string>>(
      (colorMap, { dataPoint, color }) => {
        colorMap[dataPoint] = color;
        return colorMap;
      },
      {}
    ) ?? {}
  );
};

export const toBreadCrumb = ({
  label,
  kind,
}: FilterSuggestionSecondaryInformation) => ({
  text: label,
  icon:
    kind === 'workspace'
      ? { name: IconName.WORKSPACE }
      : kind === 'email'
        ? { name: IconName.PERSON }
        : undefined,
});
const alwaysSupportedTypes: SupportedDashboardFilterType[] = [
  APIFieldType.LIST,
  APIFieldType.SELECT_MULTIPLE_LIST,
  APIFieldType.CHECKBOX,
  APIFieldType.NUMBER,
  APIFieldType.USER,
  APIFieldType.TEXT,
  APIFieldType.DATE_ONLY,
  APIFieldType.DATE_TIME,
  ReportColumnType.REFERENCE_TYPE_INCOMING,
  ReportColumnType.REFERENCE_TYPE_OUTGOING,
];
export const getSupportedDashboardFilterTypes = (
  isNewDevDashboardFilterTypesEnabled: boolean
): SupportedDashboardFilterType[] =>
  isNewDevDashboardFilterTypesEnabled
    ? alwaysSupportedTypes
    : alwaysSupportedTypes;

export const isReportFieldSupportedForDashboardFilters = (
  fieldType: APIFieldType | SupportedDashboardFilterReferenceType,
  {
    fieldKey,
    fieldsByName,
    supportedDashboardFilterTypes,
    datasourceStrategy,
  }: {
    fieldKey: string;
    fieldsByName: Record<string, MinimalFieldRepresentation>;
    supportedDashboardFilterTypes: Array<
      APIFieldType | SupportedDashboardFilterReferenceType
    >;
    datasourceStrategy?: DataSourceType;
  }
): fieldType is SupportedDashboardFilterType => {
  if (!supportedDashboardFilterTypes.includes(fieldType)) {
    return false;
  }

  const isReferenceColumnFilterType =
    widgetOperations.isReferenceColumnDashboardFilterType(fieldType);

  // Gremlin search reports do not support reference column dashboard filters.
  if (isReferenceColumnFilterType && isGremlinSearch(datasourceStrategy)) {
    return false;
  }

  if (isReferenceColumnFilterType) {
    return true;
  }

  return (
    // there might be gremlin fields, need to make sure both fieldKey and fieldType match
    fieldsByName[fieldKey]?.type === fieldType
  );
};

export const isReportComponentIdValueColumn = (fieldKey: string) =>
  ['target', 'source', 'parent'].includes(fieldKey);

const compareWidgetByLayout = (aLayout: Layout, bLayout: Layout): number =>
  aLayout!.y < bLayout!.y ||
  (aLayout!.y === bLayout!.y && aLayout!.x < bLayout!.x)
    ? -1
    : 1;

const getExpandWidget =
  (
    openFullscreenWidget: OpenFullscreenWidget,
    widgetData: FullscreenWidgetData,
    globalFilterValues?: DashboardFilterValues
  ) =>
  () =>
    openFullscreenWidget(widgetData, globalFilterValues);

export const getSortedWidgetPropsArray = ({
  dashboardData,
  openFullscreenWidget,
  contextDashboardIsUsedIn,
  dashboardMode,
  userLocale,
  colors,
  onDrilldown,
  getReportNameById,
  getComponentLinkHandler,
  getHasComponentReadPermission,
  i18nLocale,
}: {
  dashboardData: APIDashboardAttributes;
  openFullscreenWidget?: OpenFullscreenWidget;
  contextDashboardIsUsedIn: ContextDashboardIsUsedIn;
  dashboardMode: DashboardModes;
  userLocale: Locale;
  colors: Record<string, HexColor>;
  onDrilldown: OnWidgetDrilldown | undefined;
  getReportNameById: ReportFromSelectedWidgetId | undefined;
  getComponentLinkHandler: GetComponentLinkHandler | undefined;
  getHasComponentReadPermission:
    | ((scopeData: EnhancedScopeData) => HasComponentReadPermission)
    | undefined;
  i18nLocale: I18nLocale;
}): ViewModeWidgetPropsArrayWithType => {
  return dashboardData.widgets
    .sort((a, b) => compareWidgetByLayout(a.layout, b.layout))
    .map(widget =>
      widgetOperations.isReadHeaderWidgetData(widget)
        ? {
            ...widget,
            type: 'header' as const,
          }
        : widgetOperations.isReadPowerBiIframeWidgetData(widget)
          ? {
              ...getViewModePowerBiIframeWidgetProps({ widgetData: widget }),
              type: 'powerBiIframe' as const,
            }
          : widgetOperations.isReadNoteWidgetData(widget)
            ? {
                ...getViewModeNoteWidgetProps({
                  widgetData: widget,
                  expandWidget: openFullscreenWidget
                    ? getExpandWidget(
                        openFullscreenWidget,
                        widget,
                        dashboardData.globalFilterValues
                      )
                    : undefined,
                }),
                type: 'note' as const,
              }
            : dashboardMode === DashboardModes.METRICS
              ? {
                  ...getUsageMetricsModeWidgetProps({
                    dashboardId: dashboardData._id,
                    widgetData: widget,
                    userLocale,
                    colors,
                    i18nLocale,
                  }),
                  type: 'chart' as const,
                }
              : {
                  ...getViewModeChartWidgetProps({
                    dashboardId: dashboardData._id,
                    widgetData: widget,
                    contextDashboardIsUsedIn,
                    userLocale,
                    colors,
                    onDrilldown,
                    getReportNameById,
                    openFullscreenWidget,
                    getComponentLinkHandler,
                    getHasComponentReadPermission,
                    dashboardFilterParameters:
                      dashboardData.globalFilterParameters,
                    dashboardFilterValues:
                      dashboardData.globalFilterValues ?? {},
                    i18nLocale,
                  }),
                  type: 'chart' as const,
                }
    );
};
